import React from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { isSafeReturnTo } from "@/lib/oauth.utils";

interface AuthRedirectProps {
  children: React.ReactNode;
  redirectTo?: string;
  requireAuth?: boolean;
}

export const AuthRedirect: React.FC<AuthRedirectProps> = ({
  children,
  redirectTo = "/dashboard",
  requireAuth = false,
}) => {
  const { user, isInitializingAuth } = useAuth();
  const [searchParams] = useSearchParams();

  // Show loading spinner while checking authentication status
  if (isInitializingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // If requireAuth is true and user is not authenticated, redirect to login
  if (requireAuth && !user) {
    return <Navigate to="/login" replace />;
  }

  // If requireAuth is false (auth pages) and user is authenticated, redirect away.
  // Honor a safe returnTo so a logged-in user who lands on /login?returnTo=…
  // (e.g. mid OAuth-consent flow) is sent back to where they came from.
  if (!requireAuth && user) {
    const returnTo = searchParams.get("returnTo");
    const target = isSafeReturnTo(returnTo) ? (returnTo as string) : redirectTo;
    return <Navigate to={target} replace />;
  }

  // Render children if conditions are met
  return <>{children}</>;
};
